import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PbxContext from '../../../context/PbxContext';
import { accessLocalStorage, changeViewForms, changeIpRoutes, readInterfaces } from '../../../services';
import { validateRoutePayload } from '../../../utils/network';
import FormRoutesSet from './formIpSetRoutes';
import './RoutesNew.css';

function RoutesNew() {
  const getItensStateGlobal = useContext(PbxContext);
  const { toggleIsHidden } = getItensStateGlobal;
  const history = useHistory();

  const [values, setValues] = useState({});
  const [disable, setDisable] = useState(true);
  const [disableInput, setDisableInput] = useState(false);
  const [showIfaces, setShowIfaces] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [resultMessage, setResultMessage] = useState(null); // { kind: 'success'|'error', text }
  const [validationErrors, setValidationErrors] = useState([]);

  const { changeBorderImputToRedOrNormal, changeVisibityMessages } = changeViewForms;

  const validateUserLogged = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    const { user: { role } } = dataUser;
    if (role !== 'root') return history.push('/home');
    const getInterfaces = await readInterfaces();
    const interfaces = getInterfaces ? Object.keys(...getInterfaces) : [];
    const filteredInterfaces = interfaces.filter((iface) => !iface.includes('lo'));
    setShowIfaces(filteredInterfaces);
  }, [history]);

  // Atualiza estado de UI (borda + mensagem inline) por campo, com base
  // numa avaliação contextual rápida. A validação dura é feita ANTES de
  // submeter via validateRoutePayload.
  const fieldLooksOk = (name, value) => {
    if (value === '' || value == null) return null; // neutro: não pinta
    const { isValidIPv4, isValidIPv6, isValidIP, parseCidr, maskToPrefix } = require('../../../utils/network');
    if (name === 'srdest') {
      // aceita IPv4 puro, CIDR IPv4 ou CIDR IPv6
      if (parseCidr(String(value).trim())) return true;
      return isValidIPv4(value);
    }
    if (name === 'mask') {
      const v = String(value).trim();
      if (v === '') return null;
      return maskToPrefix(v) !== null;
    }
    if (name === 'gw') return isValidIP(value);
    if (name === 'metric') {
      const v = String(value).trim();
      if (v === '') return null;
      const n = Number(v);
      return Number.isInteger(n) && n >= 0 && n <= 4294967295;
    }
    if (name === 'iface') return /^[a-z][a-z0-9_.:-]{1,15}$/.test(value);
    return true;
  };

  const handleChangeValues = (item) => {
    const { name, value } = item;
    setValues((prev) => ({ ...prev, [name]: value }));

    const ok = fieldLooksOk(name, value);
    if (ok !== null) {
      changeBorderImputToRedOrNormal(ok, name);
      changeVisibityMessages(ok, `${name}-invalid-data`);
    }

    setDisable(computeMissing({ ...values, [name]: value }).length > 0);
  };

  // Calcula quais campos ainda faltam preencher / corrigir. Métrica é opcional;
  // máscara só é obrigatória se srdest for IPv4 puro (sem /). Usado tanto para
  // habilitar o botão quanto para o feedback visual.
  const computeMissing = (next) => {
    const missing = [];
    if (fieldLooksOk('srdest', next.srdest) !== true) missing.push('IP/Sub-Rede de destino');
    if (fieldLooksOk('gw', next.gw) !== true) missing.push('Gateway');
    if (fieldLooksOk('iface', next.iface) !== true) missing.push('Interface');
    const srdestHasPrefix = typeof next.srdest === 'string' && next.srdest.includes('/');
    if (!srdestHasPrefix && fieldLooksOk('mask', next.mask) !== true) missing.push('Máscara');
    if (next.metric !== undefined && next.metric !== '' && fieldLooksOk('metric', next.metric) !== true) {
      missing.push('Métrica (numérica)');
    }
    return missing;
  };

  const missingFields = computeMissing(values);

  const submit = async () => {
    setSubmitting(true);
    setResultMessage(null);
    setValidationErrors([]);

    // Normaliza payload: aceita schema legado {srdest, mask, gw, iface, metric}.
    const payload = {
      srdest: (values.srdest || '').trim(),
      mask: (values.mask || '').trim(),
      gw: (values.gw || '').trim(),
      iface: (values.iface || '').trim(),
      metric: (values.metric || '').trim(),
    };
    // Se srdest já tem /prefix, descartamos mask para evitar confusão.
    if (payload.srdest.includes('/')) delete payload.mask;

    const v = validateRoutePayload(payload);
    if (!v.ok) {
      setValidationErrors(v.errors);
      setSubmitting(false);
      return;
    }

    const resp = await changeIpRoutes([payload]);

    if (resp.ok && (resp.data.applied?.length > 0 || resp.data.existing?.length > 0)) {
      const appliedCount = resp.data.applied?.length || 0;
      const existingCount = resp.data.existing?.length || 0;
      let text = '';
      if (appliedCount > 0) text += `${appliedCount} rota(s) criada(s) com sucesso.`;
      if (existingCount > 0) text += `${existingCount > 0 ? ' ' : ''}${existingCount} rota(s) já existiam (sem alteração).`;
      const errs = (resp.data.errors || []).map((e) => e.message || e.code || 'erro');
      setResultMessage({ kind: 'success', text, warnings: resp.data.warnings || [], errors: errs });
      setDisableInput(true);
    } else if (resp.ok && resp.data.errors?.length > 0) {
      const errs = resp.data.errors.map((e) => e.message || e.code || JSON.stringify(e));
      setResultMessage({ kind: 'error', text: 'Nenhuma rota foi criada.', errors: errs });
    } else {
      // erro de transporte ou semântico do backend
      const text = resp.message || 'Falha ao criar rota.';
      const code = resp.code ? ` (${resp.code})` : '';
      setResultMessage({ kind: 'error', text: `${text}${code}` });
    }

    setSubmitting(false);
  };

  const goToList = () => history.push('/config/rede/ip/routes/lista');

  useEffect(() => {
    validateUserLogged();
  }, [validateUserLogged]);

  return (
    <div className="routes-new-container">
      <div className="routes-new-header">
        <div className="routes-new-title-wrapper">
          <svg className="routes-new-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <div>
            <h1 className="routes-new-title">Adicionar Rotas</h1>
            <p className="routes-new-subtitle">A rota é aplicada imediatamente via netplan e persiste após reboot.</p>
          </div>
        </div>
      </div>

      {validationErrors.length > 0 && (
        <div className="alerts-section">
          <div className="alert-message alert-danger">
            <div className="alert-header"><span>Validação:</span></div>
            <div className="alert-body">
              <ul>{validationErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          </div>
        </div>
      )}

      {resultMessage && (
        <div className="alerts-section">
          <div className={`alert-message ${resultMessage.kind === 'success' ? 'alert-success' : 'alert-danger'}`}>
            <div className="alert-header">
              <span>{resultMessage.kind === 'success' ? 'Sucesso' : 'Erro'}</span>
              <button className="alert-close" aria-label="close" onClick={() => { setResultMessage(null); setDisableInput(false); }}>×</button>
            </div>
            <div className="alert-body">
              <div>{resultMessage.text}</div>
              {resultMessage.warnings && resultMessage.warnings.length > 0 && (
                <>
                  <strong>Avisos:</strong>
                  <ul>{resultMessage.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </>
              )}
              {resultMessage.errors && resultMessage.errors.length > 0 && (
                <>
                  <strong>Detalhes de erro:</strong>
                  <ul>{resultMessage.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="route-form-section">
        <FormRoutesSet iface={showIfaces} handleChangeValues={handleChangeValues} disableInput={disableInput} />
      </div>

      <div className="route-actions">
        <button className="btn-configure-route" type="submit" disabled={disable || submitting} onClick={submit}>
          <svg className="btn-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {submitting ? 'Aplicando...' : 'Configurar Rota'}
        </button>
        {resultMessage && resultMessage.kind === 'success' && (
          <button className="btn-apply-reboot" type="button" onClick={goToList}>
            <span>Ver lista de rotas</span>
          </button>
        )}
      </div>
      {disable && !submitting && missingFields.length > 0 && (
        <div className="alerts-section">
          <div className="alert-message alert-info">
            <div className="alert-body">
              <strong>Para habilitar o botão, preencha:</strong>
              <ul>{missingFields.map((f, i) => <li key={i}>{f}</li>)}</ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoutesNew;
